
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Shield } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'sonner';

// Create an isolated Supabase client for impersonation to prevent affecting parent session
const isolatedSupabase = createClient(
  'https://pvaumvowzncvrdkkvnqq.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InB2YXVtdm93em5jdnJka2t2bnFxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDU5MzkwNzYsImV4cCI6MjA2MTUxNTA3Nn0.tzzUBAW0sotu9taBdFv62G3RYDAnP7C5CPluRo4Odcs',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'impersonation-session', // Isolate from main tab
    }
  }
);

const ParentAthleteImpersonation: React.FC = () => {
  const { athleteId } = useParams<{ athleteId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    authenticateAsAthlete();
  }, [athleteId]);

  const authenticateAsAthlete = async () => {
    try {
      if (!athleteId) {
        setError('Missing athlete ID');
        setIsProcessing(false);
        return;
      }

      // Get tokens from URL parameters
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');

      if (!accessToken || !refreshToken) {
        setError('Missing authentication tokens');
        setIsProcessing(false);
        return;
      }

      console.log('Setting athlete session for impersonation using isolated client...');

      // Set the session using isolated Supabase client to prevent affecting parent session
      const { error: sessionError } = await isolatedSupabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken
      });

      if (sessionError) {
        console.error('Session error:', sessionError);
        setError('Failed to authenticate as athlete');
        setIsProcessing(false);
        return;
      }

      console.log('Successfully authenticated as athlete with isolated session, redirecting to dashboard...');
      
      // Redirect to athlete dashboard
      navigate('/athlete-dashboard', { replace: true });

    } catch (error) {
      console.error('Error during impersonation:', error);
      setError('Failed to authenticate as athlete');
      setIsProcessing(false);
    }
  };

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="p-6">
            <div className="flex items-center justify-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span>Authenticating as athlete...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              <span>Authentication Failed</span>
            </CardTitle>
            <CardDescription>
              Unable to authenticate as the athlete.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {error}
              </p>
              <p className="text-xs text-gray-500">
                Please try again from the parent dashboard.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default ParentAthleteImpersonation;
